// WHAT YOU C(OPY) IS WHAT YOU P(ASTE)

import {captureException} from '@sentry/browser';
import $ from 'jquery';
import moment from 'moment';
import React from 'react';
import _ from 'underscore';

import * as benchlingUtil from 'BenchlingUtil';
import parseMolBioFile from 'notebook/integrations/molbio/parseMolBioFile';
// @ts-expect-error
import formatters from 'notebook/mediocre/formatted-text/formatters';
// @ts-expect-error
import * as selutil from 'notebook/mediocre/selection/text/SelUtil';
import {ListType} from 'notebook/notes/lists/ListConstants';
import notifications from 'notifications';
import * as logging from 'util/Logging';
import {ElementClipboardEvent} from 'util/Types';

import * as ExcelProcessor from './ExcelProcessor';
import generateTimestampedName from './generateTimestampedName';
import parseDOMColor from './parseDOMColor';
import parseTable, {ParseTableOptions} from './parseTable';
import parseText, {ParseTextOptions} from './parseText';
import * as WordProcessor from './WordProcessor';
import {INLINE, NonTableWYCIWYPPart, SKIP, WYCIWYPPart} from './WyciwypConstants';

const {BULLET, NUMBER} = ListType;
const TABLE = 'table';
const TIME = 'time';
const HEADER = 'header';
const DESC = 'desc';
const CODE = 'code';

const GOOGLE_DOCS_ID_REGEX = /^docs-internal-guid-/;

function parseTime(text: string): number {
  const duration = moment.duration();
  for (const timeSegment of Array.from(text.split(', '))) {
    const splitTimeSegment = Array.from(timeSegment.split(' '));
    const amount = parseInt(splitTimeSegment[0], 10);
    // @ts-expect-error
    duration.add(amount, splitTimeSegment[1]);
  }
  return duration.asSeconds();
}

export type ProcessNodeOptions = ProcessClipboardOptions & {
  sanitizeAndParseTD?: (td: HTMLTableDataCellElement, originalText: string) => {formula?: string};
  // If set to true, don't try to parse the content in the table as locale specific
  skipLocaleParsing?: boolean;
};

// Helper to process a single node into a list of parts
function processNode(el: HTMLElement, options: ProcessNodeOptions): Array<WYCIWYPPart> {
  // Google Docs wraps copied text in a `<b style="font-weight:normal;">`, so we ignore it and
  // process its children.
  let fmtTexts: Array<WYCIWYPPart>;
  let result;
  if (el.id != null && GOOGLE_DOCS_ID_REGEX.test(el.id)) {
    return processNodes($(el).contents(), options);
  }

  // `options.handleNode` is a function that can be passed in that will take in a node and generate
  // a custom result. If no results are returned, normal operations will continue.
  const {handleNode} = options;
  if (handleNode != null) {
    result = handleNode(el, options);
    if (result != null) {
      return [result];
    }
  }

  const optionNodeName =
    typeof options.getNodeName === 'function' ? options.getNodeName(el.nodeName) : undefined;
  const nodeName = optionNodeName != null ? optionNodeName : el.nodeName;

  // Headers and tables are parsed based on nodeName
  // Descriptions are parts derived from an LI (excluding the first part of an LI)
  // We also parse into descriptions and times based on className (copying from viewer)
  // Ignore comments.
  const {indentation} = options;
  if (Array.from(SKIP).includes(nodeName)) {
    return [];
  }
  if (Array.from(INLINE).includes(nodeName)) {
    ({fmtTexts} = parseText(el, options));
    // A function can be passed in that overrides the default isEmpty method.
    const isEmptyFn = options.isEmpty != null ? options.isEmpty : formatters.isEmpty;
    if (isEmptyFn(fmtTexts)) {
      return [];
    }
    return fmtTexts;
  }
  if (nodeName === 'TABLE' && options.allowTables) {
    const table = parseTable(el as HTMLTableElement, options);
    if (table == null) {
      notifications.error(`Sorry, we were unable to paste the table from the clipboard. \
Try copying and pasting the table again.`);
      return [];
    }
    return [
      {
        text: table,
        type: TABLE,
        formats: formatters.defaultFormats(),
        indentation,
        listType: null,
      },
    ];
  }
  if (['H1', 'H2', 'H3', 'H4', 'H5', 'H6'].includes(nodeName)) {
    ({fmtTexts} = parseText(el, options));
    if (options.allowHeaders) {
      // eslint-disable-next-line no-restricted-syntax
      return fmtTexts.map((fmtText) => _.extend({}, fmtText, {type: HEADER}));
    }
    // If headers are not allowed, fully bold the header.
    return fmtTexts.map((fmtText) => {
      const {text, formats} = formatters.formatText(fmtText.formats, {
        format: 'bold',
        partition: {before: '', selected: fmtText.text, after: ''},
        disableToggle: true,
      });
      // eslint-disable-next-line no-restricted-syntax
      return _.extend({}, fmtText, {text, formats});
    });
  }
  if ($(el).hasClass('cm-comments')) {
    return [];
  }
  const childOptions = ['OL', 'UL', 'BLOCKQUOTE'].includes(nodeName)
    ? // eslint-disable-next-line no-restricted-syntax
      _.extend({}, options, {
        indentation: indentation! + 1,
        listType: (() => {
          switch (nodeName) {
            case 'OL':
              return NUMBER;
            case 'UL':
              return BULLET;
            default:
              return null;
          }
        })(),
      })
    : options;
  const nodes = $(el).contents();
  const results = processNodes(nodes, childOptions);

  if (nodeName === 'LI') {
    // Tables cannot be added directly into bullets, so an empty line needs to be added first
    if ((results[0] != null ? results[0].type : undefined) === TABLE) {
      results.unshift({
        text: '',
        formats: formatters.defaultFormats(),
        indentation,
        listType: null,
      });
    }
    for (let i = 0; i < results.length; i++) {
      result = results[i];
      if (i === 0) {
        result.listType = options.listType;
      } else if (result.type == null) {
        result.type = DESC;
      }
    }
  }
  if ($(el).hasClass('procedure-detail')) {
    for (result of Array.from(results)) {
      if (result.type == null) {
        result.type = DESC;
      }
    }
  }
  if ($(el).hasClass('step-aggregate-time') && results.length === 1) {
    results[0].type = TIME;
  }
  return results;
}

// Helper to process a list of nodes (that are siblings) into a list of parts
function processNodes(els: JQuery | Array<HTMLElement>, options: ProcessNodeOptions): Array<WYCIWYPPart> {
  // For testing
  if (options.throwTestHTMLError) {
    throw new Error('HTML error');
  }

  const {indentation} = options;

  // If there is a BR, start a new part.
  // If an inline follows an inline, combine them.
  let results: Array<WYCIWYPPart> = [];
  let lastInline;
  let lastLineBreak;
  for (let i = 0; i < els.length; i++) {
    const el = els[i];
    const hasBr = $(el).find('br').length > 0;
    const optionNodeName =
      typeof options.getNodeName === 'function' ? options.getNodeName(el.nodeName) : undefined;
    const nodeName = optionNodeName != null ? optionNodeName : el.nodeName;
    const inline = Array.from(INLINE).includes(nodeName);
    const br = nodeName === 'BR';
    if (br) {
      // By default, do not add a newline. Instead, force the next piece of input to be on the next line.
      // If there was a previous linebreak or the last element wasn't inline, actually add a newline
      if (lastLineBreak || !lastInline) {
        results.push({text: '', formats: formatters.defaultFormats(), indentation, listType: null});
      }
      lastInline = false;
      lastLineBreak = true;
    } else {
      lastLineBreak = false;
      options.trimLeadingWhitespace = inline && !lastInline;
      let nextNodeName: string | undefined;
      if (els[i + 1] != null) {
        const nextOptionNodeName =
          typeof options.getNodeName === 'function' ? options.getNodeName(els[i + 1].nodeName) : undefined;
        nextNodeName = nextOptionNodeName != null ? nextOptionNodeName : els[i + 1].nodeName;
      }
      options.trimTrailingWhitespace =
        inline && !(els[i + 1] != null && Array.from(INLINE).includes(nextNodeName!));

      if (inline && lastInline) {
        const {fmtTexts} = parseText(el, options);
        const previous = results[results.length - 1] as NonTableWYCIWYPPart;
        // Allow a processor to override the default behavior of not combining inlines.
        if (options.shouldCombine != null && !options.shouldCombine(previous, fmtTexts[0])) {
          results.push(...Array.from(fmtTexts || []));
          continue;
        }
        const combinedFormats = formatters.joinFormats(
          previous.formats,
          fmtTexts[0].formats,
          previous.text.length
        );
        previous.text += fmtTexts[0].text;
        previous.formats = combinedFormats;

        // append the rest to results
        results.push(...Array.from(fmtTexts.slice(1) || []));
      } else {
        const processed = processNode(el, options);
        results.push(...Array.from(processed || []));
        lastInline = processed.length && inline;

        // This catches the case where a <div> (or another block level element) has
        // no content other than <br>s. In this case, we want to push a newline.
        if (processed.length === 0 && hasBr) {
          results.push({text: '', formats: formatters.defaultFormats(), indentation, listType: null});
        }
      }
    }
  }

  // Combine results before returning.
  if (options.combineFmtTexts != null) {
    results = options.combineFmtTexts(results);
  }

  const finalized = [];
  for (const result of Array.from(results)) {
    switch (result.type) {
      case TABLE:
        finalized.push(result);
        break;
      case TIME:
        if (finalized.length) {
          finalized[finalized.length - 1].time = parseTime(result.text);
        }
        break;
      default: {
        const {text, formats} = formatters.trim(result.text, result.formats);
        result.text = text;
        result.formats = formats;
        finalized.push(result);
      }
    }
  }
  return finalized;
}

function getHTMLData(clipboardData: DataTransfer): string {
  const html = clipboardData.getData('text/html');
  if (html && !html.startsWith('<')) {
    // Firefox often only outputs the contents of a text node (even if the clipboard contains full HTML),
    // so we normalize the html by wrapping it in a body tag.
    return `<body>${html}</body>`;
  } else {
    return html;
  }
}

function captureOrThrow(e: Error): void {
  if (window.debug) {
    throw e;
  } else {
    captureException(e);
  }
}

export interface BaseProcessClipboardOptions {
  indentation?: number;
  combineFmtTexts?: (results: Array<WYCIWYPPart>) => Array<WYCIWYPPart>;
  shouldCombine?: (part1: WYCIWYPPart, part2: WYCIWYPPart) => boolean;
  throwTestHTMLError?: boolean;
  trimLeadingWhitespace?: boolean;
  trimTrailingWhitespace?: boolean;
  listType?: string;
  allowHeaders?: boolean;
  allowTables?: boolean;
  getNodeName?: (nodeName: string) => string;
  handleNode?: (el: HTMLElement, options: ProcessClipboardOptions) => WYCIWYPPart;
  isEmpty?: boolean;
  parseHTML?: boolean;
  allowLinks?: boolean;
}

export type ProcessClipboardOptions = BaseProcessClipboardOptions & ParseTableOptions & ParseTextOptions;

function processClipboard(clipboard: DataTransfer, options: ProcessClipboardOptions): Array<WYCIWYPPart> {
  let data;
  if (options == null) {
    options = {};
  }
  _.defaults(options, {
    parseHTML: true,
    allowHeaders: true,
    allowLinks: true,
    allowTables: false,
    indentation: 0,
    listType: null,
  });
  const optionsWithDefaults = options as ProcessClipboardOptions & {
    parseHTML: boolean;
    allowHeaders: boolean;
    allowLinks: boolean;
    allowTables: boolean;
    indentation: number;
    listType: string | null;
  };
  // Try to parse HTML
  if (optionsWithDefaults.parseHTML && typesContains(clipboard.types, 'text/html')) {
    try {
      data = getHTMLData(clipboard);
      try {
        if (WordProcessor.isWord(data)) {
          return WordProcessor.processWord(data, processNode, optionsWithDefaults);
        } else if (optionsWithDefaults.allowTables && ExcelProcessor.isExcel(data)) {
          return ExcelProcessor.processExcel(data, processNode, optionsWithDefaults);
        }
        // eslint-disable-next-line benchlint/always-throw-from-catch
      } catch (e) {
        captureOrThrow(e);
      }
      // Fallback to HTML if Word / Excel processing errors or does not happen.
      data = filterUnwantedCharactersWithWarn(data);
      const $data = wyciwyp.jQuerySafeParseHTML(data);
      if (
        $data.filter(function (): boolean {
          // @ts-expect-error
          return !Array.from(SKIP).includes(this.nodeName);
        }).length > 0
      ) {
        return processNodes($data, optionsWithDefaults);
      }
      // eslint-disable-next-line benchlint/always-throw-from-catch
    } catch (e) {
      captureOrThrow(e);
    }
  }
  // Fallback to text if html not available and on error.
  data = clipboard.getData('text/plain');
  data = filterUnwantedCharactersWithWarn(data);

  const results = Array.from(data.split('\n')).map((row) => ({
    text: row,
    formats: formatters.defaultFormats(),
    listType: null,
    indentation: 0,
  }));
  return results;
}

/**
 * Strip a subset of control characters from `str` and show a warning if any characters were stripped.
 *
 * This avoids the issue where a pasted NUL (0x00) character ultimately cannot be persisted by the back-end
 * due to a limitation in Postgres.
 */
function filterUnwantedCharactersWithWarn(str: string): string {
  const oldLength = str.length;

  str = str.replace(/[\x00-\x07]/g, ''); // eslint-disable-line no-control-regex

  const n = oldLength - str.length;
  if (n > 0) {
    notifications.warn(`${n} invalid ${n > 1 ? 'characters were' : 'character was'} removed while pasting.`, {
      id: 'wyciwyp.filterUnwantedCharactersWithWarn',
    });
  }

  return str;
}

function typesContains(types: ReadonlyArray<string> | DOMStringList, needle: string): boolean {
  // It looks like types is sometimes (but rarely) null on Safari 9 (T6774). Instead of erroring,
  // we silently return null since it is likely that there was nothing useful for us to paste anyway.
  if (types == null) {
    return false;
  }

  // The standards are garbage when it comes to .types - FF uses a DOMStringList while Chrome and Safari
  // have an Array. Unfortunately, that means we need to use indexOf if Array and contains otherwise.
  if ((types as Array<string>).indexOf != null) {
    return (types as Array<string>).indexOf(needle) >= 0;
  } else {
    return (types as DOMStringList).contains(needle);
  }
}

/**
 * Retrieves the DataTransferItem associated with a file from a paste event if a file can be found.
 * @param  {DataTransfer} clipboardData
 * @return {DataTransferItem|null}
 */
function getClipboardItemForFile(clipboardData: DataTransfer): DataTransferItem | null | undefined {
  if (!typesContains(clipboardData.types, 'Files')) {
    // NB Safari seems to have 'image/tiff' type, but there's no item...
    return null;
  }
  return _.findWhere(clipboardData.items, {kind: 'file'});
}

/**
 * Ensure that the given `file` has a name. If `file` is a File, then it will already have a read-only name.
 * If `file` is a Blob, it will not already have a name, so we assign a name based on its mime type and the
 * current time.
 *
 * Note that for Chrome, DataTransfer.getAsFile() returns a Blob but may return a File in the future.
 * See: https://crbug.com/361145
 *
 * @param  {Blob|File} file
 */
function augmentFileWithName(file: (Blob & {name?: string}) | File): void {
  // Avoid attempting to modify the read-only `name` property on File. It no-ops in non-strict mode but raises
  // a TypeError in strict mode.
  if ((file as File).name != null) {
    return;
  }
  const fileName = generateTimestampedName();
  // For now only support image extensions. It's actually not clear where non-image data types would come
  // from, as copying from OS will just result in image of file preview.
  const extension = (() => {
    switch (file.type) {
      case 'image/jpeg':
        return 'jpg';
      case 'image/png':
        return 'png';
      case 'image/gif':
        return 'gif';
      default:
        return null;
    }
  })();
  if (extension != null) {
    (file as Blob & {name?: string}).name = `${fileName}.${extension}`;
  } else {
    (file as Blob & {name?: string}).name = fileName;
  }
}

function fileFromClipboard(clipboardData: DataTransfer): File | null {
  // If there is HTML on the clipboard, be sure it is a sole <img> tag. See T11174.
  const html = getHTMLData(clipboardData);
  if (html) {
    const parser = new DOMParser();
    const document_ = parser.parseFromString(html, 'text/html');
    const isHTMLImage = $(document_.body).children().length === 1 && $(document_.body).children().is('img');
    if (!isHTMLImage) {
      return null; // we prefer parsing HTML
    }
  }
  const molbioFile = parseMolBioFile(clipboardData);
  if (molbioFile) {
    return molbioFile;
  }
  const clipboardItem = getClipboardItemForFile(clipboardData);
  if (clipboardItem == null) {
    return null;
  }
  const file = clipboardItem.getAsFile();

  // clipboardItem can be defined with kind: 'file', but getAsFile() will still return null if
  // the file is not an image type that can be converted to a png. This happens when you copy
  // a random non-image file (e.g. a javascript file) from your Mac finder, then paste it into
  // an entry.
  if (file == null) {
    return null;
  }
  augmentFileWithName(file);
  return file;
}

function hasData(clipboardData: DataTransfer): boolean {
  if (clipboardData == null) {
    return false;
  }
  const {types} = clipboardData;
  const hasPlainText = typesContains(types, 'text/plain') && clipboardData.getData('text/plain').length > 0;
  return hasPlainText || typesContains(types, 'text/html') || getClipboardItemForFile(clipboardData) != null;
}

/**
 * Provides a warning message that is browser-aware, to be called if no data is found on the clipboard
 * when pasting. Caller can provide isMissingTypes (`not clipboardData.types?`) for a more helpful message.
 */
function warnForNoData(isMissingTypes: boolean): void {
  let message;
  if (isMissingTypes == null) {
    isMissingTypes = false;
  }
  if (benchlingUtil.chrome || isMissingTypes) {
    message = (
      <>
        Unable to paste from clipboard &#x2013; try copying and pasting again, or{' '}
        <a href="mailto:support@benchling.com">click here</a> and let us know where you are copying from.
      </>
    );
  } else {
    message = `Pasting images is not supported in your browser \u2013 use Chrome for full \
functionality, or insert image as an attachment from the toolbar.`;
  }
  notifications.warn(message, {id: 'wyciwyp.warnForNoData', duration: 10});
}

/**
 * Firefox has a bug where it doesn't put clipboard data in event.clipboardData when copy-and-pasting an
 * image, but it does properly paste if the paste event is not preventDefault'd. To workaround this bug,
 * this method can be called during a paste event, which will create a hidden DOM node, paste into it,
 * and then convert the image data into a Blob.
 * @return {jQuery.Promise} resolves with either null if no image is found or a Blob containing image data
 */
function extractImageAfterPaste(): JQueryPromise<Blob | null> {
  // Create node to receive paste
  const node = document.createElement('div');
  node.setAttribute('contentEditable', 'true');
  node.classList.add('hiddenFocusEditable');
  document.body.appendChild(node);

  const deferred = $.Deferred();

  // In the next call stack, clean up and worst case say no image was found.
  let giveUp = true;
  _.defer(() => {
    document.body.removeChild(node);
    if (giveUp) {
      deferred.resolve(null);
    }
  });

  // Manipulate selection, restore selection on complete.
  const oldElement = document.activeElement as HTMLElement;
  const oldRange = selutil.getRange();
  node.focus();
  deferred.always((image) => {
    if (oldElement != null) {
      oldElement.focus();
    }
    if (oldRange != null) {
      const selection = window.getSelection()!;
      selection.removeAllRanges();
      selection.addRange(oldRange);
    }
  });

  // Extract the image when it pastes
  node.addEventListener('DOMNodeInserted', () => {
    const img = node.querySelector('img');
    const src = img != null ? img.getAttribute('src') : undefined;
    if (src == null) {
      return;
    }
    // Only handle images with a data-uri, not a URL which would require us to hit an external server.
    if (!src.startsWith('data:image/')) {
      return;
    }

    // Use fetch to convert a data-uri into a Blob. Note: fetch is not available in all browsers, but it is in
    // Firefox, which is what this function is designed to handle.
    if (window.fetch == null) {
      return;
    }
    // Fetch is asynchronous, so it will lose to the defer above that resolves with null. We signal that we
    // are handling resolving the deferred.
    giveUp = false;
    window
      .fetch(src)
      .then((res) => {
        if (res.ok) {
          return res.blob();
        } else {
          return null;
        }
      })
      .then((blob) => {
        if (blob != null) {
          augmentFileWithName(blob);
        }
        deferred.resolve(blob);
      })
      .catch((error) => {
        logging.error(error);
        deferred.resolve(null);
      });
  });
  return deferred.promise() as JQueryPromise<Blob | null>;
}

/**
 * Given a paste event, return a Promise that resolves with a file on the clipboard (or resolves with null if
 * ultimately a file could not be found). If it is determined that there is no file on the clipboard,
 * but there is other data, this method will return null, allowing the caller to handle the event.
 *
 * Note: if a Promise is returned, the caller should /not/ access e.clipboardData in the Promise's then
 * handler, as the browser will mutate the event and clear clipboardData.
 *
 * @param  {Event} e
 * @return {Promise?}
 */
function extractImageFromPaste(e: ElementClipboardEvent): Promise<Blob | null | undefined> | null {
  // wyciwyp.fileFromClipboard will return null if there is better text/html on the clipboard.
  const file = wyciwyp.fileFromClipboard(e.clipboardData);
  if (file != null) {
    e.preventDefault();
    return new Promise((res) => {
      res(file);
    });
  }

  // If there is no data on the clipboard, it is possible that the browser is not exposing it
  // correctly, but might still have data if we let the paste go through.
  if (!wyciwyp.hasData(e.clipboardData)) {
    const isMissingTypes = e.clipboardData.types == null; // Capture relevant event data before callback.
    return Promise.resolve(wyciwyp.extractImageAfterPaste()).then((pastedFile) => {
      if (pastedFile == null) {
        wyciwyp.warnForNoData(isMissingTypes);
      }
      return pastedFile;
    });
  }
  return null;
}

/**
 * Safely parses HTML from the clipboard without executing any scripts. If an error is encountered,
 * returns an empty jQuery selector and logs the error (including the failed-to-parse HTML)
 */
function jQuerySafeParseHTML(html: string): JQuery {
  let $body;
  try {
    // jQuery will load elements into the page, executing inline JS handlers set with attributes such as
    // onload and onerror, leading to an XSS vulnerability. DOMParser does not execute these, so we parse the
    // HTML with that first, before loading it into jQuery. The HTML is never loaded into the document (it
    // stays in a fragment instead) so there is no XSS threat.
    const $html = $(new DOMParser().parseFromString(html, 'text/html'));
    // Attach the body to a fragment created from document, so that body.ownerDocument.defaultView
    // will resolve to window properly, allowing us to use $.css() in wyciwyp's processNode.
    // See: https://github.com/jquery/jquery/issues/2086
    $body = $html.find('body');
    document.createDocumentFragment().appendChild($body[0]);
    // eslint-disable-next-line benchlint/always-throw-from-catch
  } catch (e) {
    return $();
  }
  return $body;
}

const wyciwyp = {
  extractImageAfterPaste,
  extractImageFromPaste,
  getHTMLData,
  jQuerySafeParseHTML,
  typesContains,
  hasData,
  warnForNoData,
  parseDOMColor,
  processClipboard,
  fileFromClipboard,
  parseText,
  processNodes,
  TABLE,
  TIME,
  HEADER,
  DESC,
  CODE,
};

export default wyciwyp;
